import React, { useState, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { Grid, Card, Tooltip, Icon, Checkbox, FormControlLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";
import useGetDailyFormVehicleTypes from "hooks/fleet/useGetDailyFormVehicleTypes";
import useCreateDailyFormVehicleType from "hooks/fleet/useCreateDailyFormVehicleType";
import parseGraphQLError from "utils/graphQL/parseGraphQLError";
import Modal from "modules/Modal/Modal";
import useModalState from "hooks/useModalState";
import {
  getDefaultValues,
  vehicleMeterTypeValues,
  vehicleSchema,
  vehicleServiceIntervalUnitValues,
  type VehicleFormData,
} from "DDD/action-objects/VehicleEdit";

export type { VehicleFormData } from "DDD/action-objects/VehicleEdit";

interface VehicleFormProps {
  mode: "create" | "edit";
  onSubmit: SubmitHandler<VehicleFormData>;
  loading: boolean;
  onCancel: () => void;
  initialData?: VehicleFormData;
  title: string;
  submitButtonText: string;
}

export default function VehicleForm({
  mode,
  onSubmit,
  loading,
  onCancel,
  initialData,
  title,
  submitButtonText,
}: VehicleFormProps) {
  const {
    data: vehicleTypes,
    loading: loadingTypes,
    error: typesError,
  } = useGetDailyFormVehicleTypes();

  const [createVehicleType, { loading: creatingType }] = useCreateDailyFormVehicleType();

  const {
    open: createTypeModalOpen,
    onOpen: openCreateTypeModal,
    onClose: closeCreateTypeModal,
  } = useModalState();
  const [newTypeName, setNewTypeName] = useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: getDefaultValues(initialData),
  });

  const handleCreateVehicleType = useCallback(async () => {
    if (!newTypeName.trim()) return;

    const result = await createVehicleType({
      variables: {
        name: newTypeName.trim(),
      },
    });

    if (result.data?.createDailyFormVehicleType) {
      const newType = result.data.createDailyFormVehicleType;
      setValue("dailyFormVehicleTypeId", newType.id);
      setNewTypeName("");
      closeCreateTypeModal();
    }
  }, [createVehicleType, newTypeName, closeCreateTypeModal, setValue]);

  const vehicleTypeOptions = [
    {
      value: "CREATE_NEW",
      label: "Create new type...",
    },
    ...(vehicleTypes?.map((type) => ({
      value: type.id,
      label: type.name,
    })) || []),
  ];

  const meterTypeOptions = [
    ...vehicleMeterTypeValues.map((value) => ({
      value,
      label: value === "MILES" ? "Miles" : "Hours",
    })),
  ];

  const serviceIntervalUnitOptions = vehicleServiceIntervalUnitValues.map((value) => ({
    value,
    label: value.charAt(0) + value.slice(1).toLowerCase(),
  }));
  const inServiceDateTooltip =
    "The date this vehicle was first placed into active service. This can be used as the starting point for date-based maintenance schedules.";

  return (
    <>
      <MDBox mb={3}>
        <MDTypography variant="h4" fontWeight="medium">
          {title}
        </MDTypography>
        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
          * Required fields
        </MDTypography>
      </MDBox>

      <Card>
        <MDBox p={3}>
          {typesError && (
            <MDAlert color="error" mb={2}>
              {parseGraphQLError(typesError)}
            </MDAlert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Vehicle Name *
                  </MDTypography>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: "Vehicle name is required" }}
                    render={({ field }) => (
                      <MDInput
                        {...field}
                        placeholder="Enter vehicle name"
                        fullWidth
                        required
                        error={!!errors.name}
                      />
                    )}
                  />
                  {errors.name && (
                    <MDTypography variant="caption" color="error" mt={1}>
                      {errors.name.message}
                    </MDTypography>
                  )}
                </MDBox>
              </Grid>

              <Grid item xs={12} md={3}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Year
                  </MDTypography>
                  <Controller
                    name="year"
                    control={control}
                    render={({ field }) => (
                      <>
                        <MDInput
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e: any) =>
                            field.onChange(e.target.value === "" ? null : Number(e.target.value))
                          }
                          type="number"
                          placeholder="Year"
                          fullWidth
                          error={!!errors.year}
                        />
                        {errors.year && (
                          <MDTypography variant="caption" color="error" mt={1}>
                            {errors.year.message}
                          </MDTypography>
                        )}
                      </>
                    )}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12} md={3}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Make
                  </MDTypography>
                  <Controller
                    name="make"
                    control={control}
                    render={({ field }) => <MDInput {...field} placeholder="Make" fullWidth />}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12} md={3}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Model
                  </MDTypography>
                  <Controller
                    name="model"
                    control={control}
                    render={({ field }) => <MDInput {...field} placeholder="Model" fullWidth />}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12} md={3}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Vehicle Type
                  </MDTypography>
                  <CustomSelect
                    data={vehicleTypeOptions}
                    label="Vehicle Type"
                    placeholder="Select vehicle type"
                    fullWidth
                    control={control}
                    name="dailyFormVehicleTypeId"
                    onChange={(e) => {
                      if (e.target.value === "CREATE_NEW") {
                        openCreateTypeModal();
                        setValue("dailyFormVehicleTypeId", null);
                      }
                    }}
                  />
                  {errors.dailyFormVehicleTypeId && (
                    <MDTypography variant="caption" color="error" mt={1}>
                      {errors.dailyFormVehicleTypeId.message}
                    </MDTypography>
                  )}
                </MDBox>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    License Plate
                  </MDTypography>
                  <Controller
                    name="licensePlate"
                    control={control}
                    render={({ field }) => <MDInput {...field} placeholder="Plate" fullWidth />}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    VIN
                  </MDTypography>
                  <Controller
                    name="vin"
                    control={control}
                    render={({ field }) => <MDInput {...field} placeholder="VIN" fullWidth />}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <MDTypography variant="h6" fontWeight="medium">
                  Registration & Compliance
                </MDTypography>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Registration State
                  </MDTypography>
                  <Controller
                    name="registrationState"
                    control={control}
                    render={({ field }) => <MDInput {...field} placeholder="State" fullWidth />}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Registration Expiration
                  </MDTypography>
                  <Controller
                    name="registrationExpirationDate"
                    control={control}
                    render={({ field }) => (
                      <MDInput
                        {...field}
                        value={field.value ?? ""}
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <MDTypography variant="h6" fontWeight="medium">
                  Service Defaults
                </MDTypography>
              </Grid>

              <Grid item xs={12} md={4}>
                <MDBox mb={2}>
                  <MDBox display="flex" alignItems="center" gap={0.5} mb={1}>
                    <MDTypography variant="body2" fontWeight="medium">
                      In Service Date
                    </MDTypography>
                    <Tooltip title={inServiceDateTooltip} placement="top" arrow>
                      <Icon fontSize="small" color="action" sx={{ cursor: "help" }}>
                        info
                      </Icon>
                    </Tooltip>
                  </MDBox>
                  <Controller
                    name="inServiceDate"
                    control={control}
                    render={({ field }) => (
                      <MDInput
                        {...field}
                        value={field.value ?? ""}
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Meter Type
                  </MDTypography>
                  <CustomSelect
                    data={meterTypeOptions}
                    label="Meter Type"
                    fullWidth
                    control={control}
                    name="meterType"
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Current Meter
                  </MDTypography>
                  <Controller
                    name="currentMeterReading"
                    control={control}
                    render={({ field }) => (
                      <>
                        <MDInput
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e: any) =>
                            field.onChange(e.target.value === "" ? null : Number(e.target.value))
                          }
                          type="number"
                          placeholder="Current meter"
                          fullWidth
                          error={!!errors.currentMeterReading}
                        />
                        {errors.currentMeterReading && (
                          <MDTypography variant="caption" color="error" mt={1}>
                            {errors.currentMeterReading.message}
                          </MDTypography>
                        )}
                      </>
                    )}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Default Service Interval Value
                  </MDTypography>
                  <Controller
                    name="defaultServiceIntervalValue"
                    control={control}
                    render={({ field }) => (
                      <>
                        <MDInput
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e: any) =>
                            field.onChange(e.target.value === "" ? null : Number(e.target.value))
                          }
                          type="number"
                          placeholder="Interval value"
                          fullWidth
                          error={!!errors.defaultServiceIntervalValue}
                        />
                        {errors.defaultServiceIntervalValue && (
                          <MDTypography variant="caption" color="error" mt={1}>
                            {errors.defaultServiceIntervalValue.message}
                          </MDTypography>
                        )}
                      </>
                    )}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Default Service Interval Unit
                  </MDTypography>
                  <CustomSelect
                    data={serviceIntervalUnitOptions}
                    label="Service Interval Unit"
                    fullWidth
                    control={control}
                    name="defaultServiceIntervalUnit"
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Notes
                  </MDTypography>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <MDInput {...field} multiline rows={3} placeholder="Vehicle notes" fullWidth />
                    )}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                      label="Vehicle is active"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <MDBox display="flex" justifyContent="flex-end" gap={2}>
                  <MDButton
                    variant="outlined"
                    color="error"
                    onClick={onCancel}
                    disabled={loading}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="success"
                    type="submit"
                    disabled={loading || loadingTypes}
                  >
                    {loading
                      ? `${mode === "create" ? "Creating" : "Updating"}...`
                      : submitButtonText}
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </form>
        </MDBox>
      </Card>

      <Modal
        open={createTypeModalOpen}
        onClose={closeCreateTypeModal}
        styles={{ width: "550px", height: "300px" }}
      >
        <MDBox p={2}>
          <MDTypography variant="h6" fontWeight="medium" mb={5}>
            Create New Vehicle Type
          </MDTypography>

          <MDBox mb={2}>
            <MDInput
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="Enter vehicle type name"
              fullWidth
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleCreateVehicleType();
                }
              }}
            />
          </MDBox>

          <MDBox display="flex" justifyContent="flex-end" gap={2}>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={closeCreateTypeModal}
              disabled={creatingType}
              size="small"
            >
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleCreateVehicleType}
              disabled={creatingType || !newTypeName.trim()}
              size="small"
            >
              {creatingType ? "Creating..." : "Create Type"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Modal>
    </>
  );
}
